import { ZfDialog, ZfDialogContent, ZfDialogHeader, ZfIcon, ZfListItem, ZfLoadingSpinner, ZfStack, ZfStatusLabel } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';
import { useClient, usePartnersByBrUuid, useStoreClient } from '../../api';
import { useWealthPlanningNavigate } from '../../hooks/useWealthPlanningNavigate';
import { PartnerErrorScreen } from '../topic/active-session/PartnerErrorScreen';

export const PartnerByBrUuidLayout: FunctionComponent = () => {
    const { t } = useTranslation();
    const { businessRelationshipUuid, partnerUuid, navigate, isRerender } = useWealthPlanningNavigate();

    const { data: partners, isLoading, isSuccess, isError } = usePartnersByBrUuid(businessRelationshipUuid, !partnerUuid);
    const { data: partner } = useClient(partnerUuid);
    const storeClient = useStoreClient();
    const [partnerSelected, setPartnerSelected] = useState(false);

    const selectPartner = useCallback(
        (partnerUuid: string) => {
            storeClient.mutate(partnerUuid, { onSuccess: () => navigate(`/${partnerUuid}`) });
            setPartnerSelected(true);
        },
        [storeClient, navigate],
    );

    useEffect(() => {
        if (partnerSelected) return;
        if (isRerender) selectPartner(partnerUuid);
        else if (isSuccess && partners.length === 1) selectPartner(partners[0]?.partnerUuid);
    }, [partners, partnerUuid, partner, selectPartner, isRerender, partnerSelected, isSuccess]);

    return (
        <>
            {isLoading ? <ZfLoadingSpinner style={{ margin: 'auto' }} /> : null}
            {partnerUuid ? <Outlet /> : null}
            {isError ? (
                <PartnerErrorScreen
                    businessRelationshipUuid={businessRelationshipUuid}
                    showWhatYouCanTrySection={false}
                    headingTextKey={'legalPartnerLoadError'}
                    warningTextKey={'failureLoadLegalPartner'}
                />
            ) : null}
            {partners?.length === 0 ? (
                <PartnerErrorScreen
                    businessRelationshipUuid={businessRelationshipUuid}
                    showWhatYouCanTrySection={true}
                    headingTextKey={'unauthorisedAccess'}
                    warningTextKey={'failureLoadPartner'}
                />
            ) : null}
            <ZfDialog show={!partnerUuid && !!partners?.length && partners.length > 1} size="m" showCloseButton={false} disableLightDismiss={true}>
                <ZfDialogHeader>{t('multiplePartnerDialogHeader')}</ZfDialogHeader>
                <ZfDialogContent>
                    <div className="multiple-partners-dialog-content">
                        <span className="multiple-partners-dialog-content__title">{t('multiplePartnersDialogTitle')}</span>
                        <span className="multiple-partners-dialog-content__description">{t('multiplePartnersDialogDescription')}</span>
                        <div className="multiple-partners-dialog-content__partner-list">
                            {partners?.map((partner) => (
                                <ZfListItem
                                    key={partner.partnerId}
                                    description={`${partner.fullName} | ${t('multiplePartnersDialogPartnerId')} ${partner.partnerId}`}
                                    size="large"
                                    onClick={() => selectPartner(partner.partnerUuid)}
                                >
                                    <ZfStack slot="trailing" direction="row">
                                        {partner.activeSession && (
                                            <ZfStatusLabel label={t('sessionActive')} color="green" style={{ position: 'relative', top: '1px' }} />
                                        )}
                                        <ZfIcon name="chevron_right" />
                                    </ZfStack>
                                </ZfListItem>
                            ))}
                        </div>
                    </div>
                </ZfDialogContent>
            </ZfDialog>
        </>
    );
};
